// Widget "Respaldo" — alcance de todo el consultorio (no de un paciente en
// particular): descarga el Bundle FHIR completo de todos los pacientes como
// respaldo operativo. El cumplimiento del Art. 11 (derecho del paciente a su
// copia completa, sección 8.7) se cubre por paciente, en la ficha
// (paciente/BotonCopiaHistoria.tsx, ruta "historia") — este widget ya no
// duplica esa función a nivel de todo el tenant.
// BA-C-10: SOLO se usa en el panel del administrador del HCE (`token` = sesión
// propia de hce_users, que no tiene login en Medplum — ver exportacionAutorizada
// en hce.ts). Antes también estaba en el Dashboard de Personal Médico, pero el
// servidor ya no entrega esta descarga a un profesional (403).
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { descargarBackupHce } from '@/lib/hceVault';

type Estado = 'idle' | 'respaldando';

export function ExportacionYRespaldo({ token }: { token: string }): JSX.Element {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<Estado>('idle');
  const [mensaje, setMensaje] = useState<{ texto: string; error: boolean; incompleto?: boolean } | null>(null);

  async function respaldar(): Promise<void> {
    setEstado('respaldando');
    setMensaje(null);
    try {
      const resultado = await descargarBackupHce(token);
      // El servidor entrega igual lo que pudo leer, pero avisa si omitió la ficha de algún paciente: no debe darse por una copia íntegra.
      if (resultado.incompleta) setMensaje({ texto: t('dashboard.exportacion.backupIncompleto', { omitidos: resultado.omitidos }), error: false, incompleto: true });
      else setMensaje({ texto: resultado.sha256 ? t('dashboard.exportacion.backupListoConHash', { sha256: resultado.sha256 }) : t('dashboard.exportacion.backupListo'), error: false });
    } catch (err) {
      setMensaje({ texto: err instanceof Error ? err.message : t('dashboard.exportacion.backupError'), error: true });
    } finally {
      setEstado('idle');
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <h2 className="text-sm font-medium text-hce-ink">{t('dashboard.exportacion.titulo')}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{t('dashboard.exportacion.descripcion')}</p>
      <div className="mt-2 space-y-2">
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => void respaldar()} disabled={estado !== 'idle'}>
          {estado === 'respaldando' ? t('dashboard.exportacion.backupGenerando') : t('dashboard.exportacion.backupBoton')}
        </Button>
      </div>
      {mensaje && (
        <Alert variant={mensaje.error ? 'red' : mensaje.incompleto ? 'orange' : 'default'} className="mt-2">
          <AlertDescription>{mensaje.texto}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
